import { ChecklistLead, DailyDownloads, PracticeTypeDistribution } from '@/types/leads.types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { useMemo } from 'react';

interface LeadChartsProps {
  leads: ChecklistLead[];
}

const practiceTypeLabels: Record<string, string> = {
  hausarzt: 'Hausarzt',
  zahnarzt: 'Zahnarzt',
  facharzt: 'Facharzt',
  mvz: 'MVZ',
  klinik: 'Klinik',
};

export const LeadCharts = ({ leads }: LeadChartsProps) => {
  const dailyDownloads: DailyDownloads[] = useMemo(() => {
    const last30Days = Array.from({ length: 30 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (29 - i));
      return date;
    });

    return last30Days.map(date => {
      const dateStr = date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
      const count = leads.filter(lead => {
        const leadDate = new Date(lead.created_at);
        return leadDate.toDateString() === date.toDateString();
      }).length;

      return {
        date: dateStr,
        downloads: count,
      };
    });
  }, [leads]);

  const practiceTypeDistribution: PracticeTypeDistribution[] = useMemo(() => {
    const distribution: Record<string, number> = {};
    
    leads.forEach(lead => {
      const type = lead.practice_type || 'other';
      distribution[type] = (distribution[type] || 0) + 1;
    });

    return Object.entries(distribution)
      .map(([type, count]) => ({
        type: practiceTypeLabels[type] || type,
        count,
        percentage: (count / leads.length) * 100,
      }))
      .sort((a, b) => b.count - a.count);
  }, [leads]);

  const chartConfig = {
    downloads: {
      label: 'Downloads',
      color: 'hsl(var(--primary))',
    },
    count: {
      label: 'Anzahl',
      color: 'hsl(var(--primary))',
    },
  };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Download-Trend (30 Tage)</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer config={chartConfig} className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailyDownloads}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="date" 
                  className="text-xs"
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                />
                <YAxis 
                  className="text-xs"
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Line 
                  type="monotone" 
                  dataKey="downloads" 
                  stroke="hsl(var(--primary))" 
                  strokeWidth={2}
                  dot={{ fill: 'hsl(var(--primary))' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Praxistypen-Verteilung</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer config={chartConfig} className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={practiceTypeDistribution}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="type" 
                  className="text-xs"
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                />
                <YAxis 
                  className="text-xs"
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar 
                  dataKey="count" 
                  fill="hsl(var(--primary))" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  );
};
